'use client';

import { type MouseEvent, useEffect, useState } from 'react';

import { useRouter } from 'next/navigation';

import { useShareDialog } from '@ps/features/sharing/useShareDialog';
import { VideoContent } from '@ps/features/video-player/components/VideoContent';
import { VideoClipsProvider } from '@ps/features/video-player/contexts/video-player/provider';
import {
  type BasicUserProfileInfo,
  type ClipReportReason,
  type PopClipVideoInfo,
  fetchClipInfo,
  handleReportClip,
} from '@ps/features/video-player/functions';
import type { BioContext } from '@ps/functions/bio/bioContext';
import type { CreatorInfo } from '@ps/types/creator';
import { useLogin } from '@ps/ui/contexts/auth';
import { useCurrentUserProfile } from '@ps/ui/contexts/user-profile';
import { Layers, Play } from 'lucide-react';

import type { CourseClipPurchaseData } from '../../../courses/visitor/functions/courseClipPurchaseData';
import { useCourseClipUnlock } from '../../../courses/visitor/functions/useCourseClipUnlock';
import {
  ClipAccessBoundary,
  ClipAccessMonitor,
  ClipAccessOverlay,
} from '../../shared/player/ClipAccessGate';
import { ClipPlayerCard } from './ClipPlayerCard';

interface Props {
  sellerId: string;
  /** The page's bio context — threaded to the player so a tagged product opens
   *  at the creator's PDP URL (custom-domain aware). */
  bioContext: BioContext;
  /** The single clip to play (the URL's `video_id`). */
  clipId: string;
  /** Detail page for this clip's owner (series / course / the All-Videos grid),
   *  and its name. */
  detailHref: string;
  detailLabel: string;
  /** The course's derived listing — set for course clips so a purchaser plays
   *  unlocked (the clip access gate treats a bought course like the owner). */
  courseListingId?: string;
  /** Standalone-purchase facts for a purchasable course — renders the "Unlock
   *  this Course for $X" button on a locked clip's paywall (RN parity). */
  coursePurchase?: CourseClipPurchaseData;
  /** Creator display name + avatar, for the guest login modal's brand chrome (the
   *  bio-home LINK is built inside the player, from `bioContext`). */
  bioLabel: string;
  bioAvatar?: string;
  /**
   * Absolute, shareable permalink for THIS clip (built server-side from the
   * page's `BioContext` + owner slug — custom-domain aware). Wired to the
   * in-player **Share** action; omit and Share only pauses (no URL to share).
   */
  shareUrl?: string;
}

/**
 * Standalone single-clip page — the hard-nav / deep-link / refresh target for
 * `.../videos/<video_id>` (the intercepted overlay is the swipe deck; this is
 * the focus view). One non-scrollable video, comments beside it on desktop
 * (open by default) / bottom sheet on mobile; centered card, full-screen on
 * mobile.
 *
 * Every way OUT lives in the player's own chrome, so the page adds no duplicate
 * link row of its own: the back arrow (top-left) climbs to `detailHref`, the
 * footer's owner pill links to the owning series / course / All-Videos grid, and
 * the creator avatar + `@name` open the bio home (`openProfile`).
 */
export function ClipVideoPage({
  sellerId,
  bioContext,
  clipId,
  detailHref,
  detailLabel,
  courseListingId,
  coursePurchase,
  bioLabel,
  bioAvatar,
  shareUrl,
}: Props) {
  const router = useRouter();
  const courseUnlock = useCourseClipUnlock(coursePurchase, bioContext);
  const { openShare } = useShareDialog();
  const login = useLogin();
  // Creator brand for the login modal when a guest taps a gated action.
  const creatorLoginInfo: CreatorInfo = {
    id: sellerId,
    urlCode: bioContext.urlCode,
    name: bioLabel,
    avatar: bioAvatar,
  };
  const [clip, setClip] = useState<PopClipVideoInfo | null>(null);
  const [status, setStatus] = useState<'loading' | 'error' | 'ready'>(
    'loading',
  );
  const [showComments, setShowComments] = useState(false);

  const profile = useCurrentUserProfile();
  const resolvedUserId = profile?.id;
  const currentUserMeta: BasicUserProfileInfo | null = profile
    ? {
        userId: profile.id,
        fullName: `${profile.firstName} ${profile.lastName}`.trim(),
        avatar: profile.avatar,
        username: profile.username,
      }
    : null;

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setClip(null);
    fetchClipInfo(sellerId, clipId)
      .then(result => {
        if (cancelled) return;
        setClip(result);
        setStatus(result ? 'ready' : 'error');
      })
      .catch(loadError => {
        if (cancelled) return;
        console.error('Failed to load clip:', loadError);
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [sellerId, clipId]);

  // Comments open by default on desktop; closed on mobile (tap to open the
  // bottom sheet). Resolved after mount — before the clip finishes loading —
  // so the card's first paint already has the right state.
  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      window.matchMedia('(min-width: 640px)').matches
    ) {
      setShowComments(true);
    }
  }, []);

  // Single-screen page — never scrolls; lock the document so mobile overscroll
  // can't drag the page behind the video.
  useEffect(() => {
    const { body: docBody } = document;
    const html = document.documentElement;
    const prevBody = docBody.style.overflow;
    const prevHtml = html.style.overflow;
    docBody.style.overflow = 'hidden';
    html.style.overflow = 'hidden';
    return () => {
      docBody.style.overflow = prevBody;
      html.style.overflow = prevHtml;
    };
  }, []);

  // Leave the video page via a hard REPLACE (not push): the standalone player is
  // fragile when restored by browser Back under cacheComponents' <Activity> — it
  // comes back stuck on its loading spinner. Replacing drops it from history so
  // Back skips past it (the terminal-nav fix used in checkout, vercel/next.js
  // #85502). Modified clicks keep the anchor default (open in a new tab).
  const leaveTo = (href: string) => (event: MouseEvent) => {
    if (
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      event.button !== 0
    ) {
      return;
    }
    event.preventDefault();
    window.location.replace(href);
  };

  // The in-video back arrow (`VideoOverlay`, top-left — the same chrome the swipe
  // player shows). Pops in-app history when there is any; a cold deep link has
  // none, so it climbs to the clip's owner instead.
  const handleBack = () => {
    if (typeof window !== 'undefined' && window.history.length > 1) {
      router.back();
      return;
    }
    router.push(detailHref);
  };

  const ownerLink = (
    <a
      href={detailHref}
      onClick={event => {
        event.stopPropagation();
        leaveTo(detailHref)(event);
      }}
      className="bg-raise/15 text-content-1 hover:bg-raise/25 mt-1 inline-flex w-fit max-w-full items-center gap-1.5 rounded-full px-2.5 py-1 text-[12px] font-medium transition"
    >
      <Layers className="h-3 w-3 shrink-0" aria-hidden />
      <span className="truncate">{detailLabel}</span>
    </a>
  );

  return (
    <div className="text-content-1 bg-surface-canvas relative flex h-dvh w-full items-center justify-center overflow-hidden">
      {status === 'loading' ? (
        <div className="border-raise/30 h-8 w-8 animate-spin rounded-full border-2 border-t-white" />
      ) : status === 'error' || !clip ? (
        <div className="flex flex-col items-center gap-3 px-6 text-center">
          <div className="bg-raise/10 flex h-12 w-12 items-center justify-center rounded-full">
            <Play className="text-raise/50 h-5 w-5" />
          </div>
          <p className="text-raise/60 text-sm">This video isn’t available.</p>
          <a
            href={detailHref}
            onClick={leaveTo(detailHref)}
            className="text-content-1 text-sm font-medium underline"
          >
            {detailLabel}
          </a>
        </div>
      ) : (
        <div className="flex flex-col gap-3 max-sm:h-full max-sm:w-full sm:w-fit">
          {/* Card — single video + comments. */}
          <ClipAccessBoundary
            sellerId={sellerId}
            courseListingId={courseListingId}
            courseSlug={coursePurchase?.courseSlug}
            courseUnlock={courseUnlock}
            onDismiss={() => window.location.replace(detailHref)}
          >
            <ClipPlayerCard
              commentsFor={showComments ? clip : null}
              currentUserId={resolvedUserId}
              currentUserMeta={currentUserMeta}
              onCloseComments={() => setShowComments(false)}
            >
              {/* A single, non-scrollable clip — no deck. */}
              <VideoClipsProvider
                initialIndex={0}
                bioContext={bioContext}
                editable={false}
                // Hide Share when no permalink was supplied — otherwise the
                // menu item would no-op.
                canShare={shareUrl != null}
                onAction={data => {
                  // Guest tapped a gated action (Like, …) — open the login
                  // modal, then return here so they can re-tap once signed in.
                  if (data.type === 'requireLogin') {
                    login({ creatorInfo: creatorLoginInfo });
                    return;
                  }
                  if (data.type === 'reportVideo') {
                    void handleReportClip({
                      video: clip,
                      reason: data.reason as ClipReportReason,
                      currentUserId: resolvedUserId,
                      promptLogin: () =>
                        login({ creatorInfo: creatorLoginInfo }),
                    });
                    return;
                  }
                  // In-player Share — open the share dialog on this
                  // clip's absolute permalink (built server-side).
                  // No URL supplied → Share only pauses (prior behavior).
                  if (data.type === 'shareVideo' && shareUrl) {
                    openShare({
                      url: shareUrl,
                      title: clip.videoTitle ?? '',
                    });
                  }
                }}
              >
                <ClipAccessMonitor videos={[clip]} />
                <VideoContent
                  video={clip}
                  show
                  play
                  focused
                  active
                  currentUserId={resolvedUserId}
                  onBack={handleBack}
                  onOpenComments={() => setShowComments(true)}
                  ownerLink={ownerLink}
                  className="h-full w-full"
                />
              </VideoClipsProvider>
              <ClipAccessOverlay />
            </ClipPlayerCard>
          </ClipAccessBoundary>
        </div>
      )}
    </div>
  );
}
